import { ComponentType } from '@angular/cdk/portal';
import { InjectionToken, Provider } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import {
  AmplifyService,
  ForgotPasswordActions,
  MfaTotpVerificationFlow,
  NotificationActions,
  CHANGE_EMAIL_DIALOG,
  CHANGE_PASSWORD_DIALOG,
  COMPANY_CONFIGURE_AUTHENTICATOR_DIALOG,
  CONFIRM_EMAIL_DIALOG,
  CONFIRM_SMS_DIALOG,
  DialogOpener,
  ENTER_PHONE_NUMBER_DIALOG,
  FORGOT_PASSWORD_DIALOG,
  FORGOT_PASSWORD_SUBMIT_DIALOG,
  SPAN_CONTROL,
  TEMP_PASSWORD_DIALOG,
  TOTP_CHANGE_AUTH_APP_DIALOG,
  TOTP_QR_CODE_DIALOG,
  TOTP_REMOVE_AUTHENTICATOR_DIALOG,
  VALIDATE_CREDENTIALS_DIALOG,
  VERIFY_PASSWORD_DIALOG,
} from '@ninety/platform/state';
import { TotpActions } from '@ninety/platform/state/app-global/auth/auth.actions';
import {
  AUTH_DIALOG_DISPATCH,
  AUTH_NOTIFY,
  AUTH_TOTP_SETUP,
  ChangeEmailDialogComponent,
  ChangePasswordDialogComponent,
  CompanyConfigureAuthenticatorDialogComponent,
  ConfirmEmailDialogComponent,
  ConfirmSmsDialogComponent,
  EnterPhoneNumberDialogComponent,
  ForgotPasswordDialogComponent,
  ForgotPasswordSubmitDialogComponent,
  SUBMIT_FORGOT_PASSWORD_EMAIL,
  SUBMIT_TOTP_SETUP_CODE,
  TempPasswordDialogComponent,
  TotpChangeAuthAppDialogComponent,
  TotpQrCodeDialogComponent,
  TotpRemoveAuthenticatorAppDialogComponent,
  ValidateCredentialsDialogComponent,
  VerifyPasswordDialogComponent,
  type AuthDialogDispatch,
  type AuthNotify,
  type SubmitForgotPasswordEmail,
  type SubmitTotpSetupCode,
} from '@ninety/ui/core';
import { SpanService } from '@ninety/ui/legacy/core/vendor/opentelemetry/span.service';

/**
 * Binds the auth dialog-opener tokens (DEV-26105 T6a, Option B) to factories that open the concrete
 * dialog components via `MatDialog`. The dialog-opening effects live in `platform/state` and inject
 * only the tokens; this composition-root wiring is the single place that depends on both the tokens
 * and the `shared/ui/core` components, so the `platform/state -> shared/ui/core` edge stays severed.
 */
function provideDialog<In, Out>(
  token: InjectionToken<DialogOpener<In, Out>>,
  component: ComponentType<unknown>
): Provider {
  return {
    provide: token,
    useFactory:
      (matDialog: MatDialog): DialogOpener<In, Out> =>
      (config?: MatDialogConfig<In>) =>
        matDialog.open<unknown, In, Out>(component, config),
    deps: [MatDialog],
  };
}

export const AUTH_DIALOG_PROVIDERS: Provider[] = [
  provideDialog(CHANGE_EMAIL_DIALOG, ChangeEmailDialogComponent),
  provideDialog(CHANGE_PASSWORD_DIALOG, ChangePasswordDialogComponent),
  provideDialog(CONFIRM_EMAIL_DIALOG, ConfirmEmailDialogComponent),
  provideDialog(CONFIRM_SMS_DIALOG, ConfirmSmsDialogComponent),
  provideDialog(ENTER_PHONE_NUMBER_DIALOG, EnterPhoneNumberDialogComponent),
  provideDialog(FORGOT_PASSWORD_DIALOG, ForgotPasswordDialogComponent),
  provideDialog(FORGOT_PASSWORD_SUBMIT_DIALOG, ForgotPasswordSubmitDialogComponent),
  provideDialog(TEMP_PASSWORD_DIALOG, TempPasswordDialogComponent),
  provideDialog(TOTP_QR_CODE_DIALOG, TotpQrCodeDialogComponent),
  provideDialog(TOTP_CHANGE_AUTH_APP_DIALOG, TotpChangeAuthAppDialogComponent),
  provideDialog(TOTP_REMOVE_AUTHENTICATOR_DIALOG, TotpRemoveAuthenticatorAppDialogComponent),
  provideDialog(COMPANY_CONFIGURE_AUTHENTICATOR_DIALOG, CompanyConfigureAuthenticatorDialogComponent),
  provideDialog(VALIDATE_CREDENTIALS_DIALOG, ValidateCredentialsDialogComponent),
  provideDialog(VERIFY_PASSWORD_DIALOG, VerifyPasswordDialogComponent),
  // Binds the span-control abstraction to the real SpanService so the auth effects (now in
  // platform/state) can trace without depending on shared/ui/core's otel vendor module. (DEV-26105 T6a)
  { provide: SPAN_CONTROL, useExisting: SpanService },

  // DEV-26530 Part 8c: the auth dialogs moved into `@ninety/ui/core` and no longer import
  // `@ninety/platform/state`. These bindings supply the store dispatches and the Amplify TOTP-setup
  // service the dialogs previously reached for directly, keeping the `shared/ui/core -> platform/state`
  // edge severed.
  { provide: AUTH_TOTP_SETUP, useExisting: AmplifyService },
  {
    provide: AUTH_NOTIFY,
    useFactory:
      (store: Store): AuthNotify =>
      (message: string) =>
        store.dispatch(NotificationActions.notify({ message })),
    deps: [Store],
  },
  {
    provide: SUBMIT_TOTP_SETUP_CODE,
    useFactory:
      (store: Store): SubmitTotpSetupCode =>
      (code: string) =>
        store.dispatch(TotpActions.submitTotpCode({ code, workflow: MfaTotpVerificationFlow.VERIFY_TOTP_SETUP })),
    deps: [Store],
  },
  {
    provide: SUBMIT_FORGOT_PASSWORD_EMAIL,
    useFactory:
      (store: Store): SubmitForgotPasswordEmail =>
      args =>
        store.dispatch(ForgotPasswordActions.submitEmailForReset(args)),
    deps: [Store],
  },
  {
    provide: AUTH_DIALOG_DISPATCH,
    useFactory:
      (store: Store): AuthDialogDispatch =>
      action =>
        store.dispatch(action),
    deps: [Store],
  },
];
